import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BannersnackCreative, Creative, DisplayCreative } from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import { ArchiveModalV1, ContextMenu, toast } from '@feathr/components';
import CreativeDuplicateModal from '@feathr/extender/components/CreativeDuplicateModal';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { getIconForAction, useToggle } from '@feathr/hooks';

import DownloadBannersnackFileMenuItem from '../DownloadBannersnackFileMenuItem';

interface IProps {
  creative: Creative;
}

function EventCreativesOptions({ creative }: Readonly<IProps>): JSX.Element {
  const { Creatives } = useStore();
  const localUrl = useLocalUrl();
  const [isArchiveModalOpen, toggleArchiveModalOpen] = useToggle(false);
  const [isDuplicateModalOpen, toggleIsDuplicateModalOpen] = useToggle(false);
  const { t } = useTranslation();

  async function handleArchive(): Promise<void> {
    try {
      await creative.archive();
      toast(t('Archived "{{name}}".', { name: creative.get('name') }), { type: 'success' });
      toggleArchiveModalOpen();
    } catch (e) {
      toast(t("Couldn't archive creative. Try again."), {
        type: 'error',
      });
    }
  }

  function handleDuplicate(): void {
    Creatives.refreshApiCache();
  }

  const type = creative.get('_cls');
  const animated = CreativeClass.DisplayBannersnack
    ? (creative as BannersnackCreative).get('animated')
    : false;

  return (
    <>
      <ContextMenu buttonType={'icon'} position={'left-start'}>
        <ContextMenu.Item
          // We don't want to be able to update facebook creatives because they require a campaign connection to be uploaded to facebook
          disabled={creative.isUpdating || creative.isFacebook}
          link={localUrl(creative.getItemUrl('edit'))}
          prefix={getIconForAction('edit')}
          tooltip={creative.isFacebook ? t('Facebook creatives cannot be edited') : undefined}
        >
          {t('Edit')}
        </ContextMenu.Item>
        <ContextMenu.Item
          // We don't want to be able to duplicate facebook creatives because they require a campaign connection to be uploaded to facebook
          disabled={creative.isUpdating || creative.isFacebook}
          onClick={toggleIsDuplicateModalOpen}
          prefix={getIconForAction('duplicate')}
          tooltip={creative.isFacebook ? t('Facebook creatives cannot be duplicated') : undefined}
        >
          {t('Duplicate')}
        </ContextMenu.Item>
        <ContextMenu.Divider />
        {type === CreativeClass.DisplayBannersnack && (
          <>
            <DownloadBannersnackFileMenuItem
              creative={creative as BannersnackCreative}
              type={'jpg'}
            />
            <DownloadBannersnackFileMenuItem
              creative={creative as BannersnackCreative}
              type={'png'}
            />
            <DownloadBannersnackFileMenuItem
              creative={creative as BannersnackCreative}
              type={'pdf'}
            />
            {animated && (
              <>
                <DownloadBannersnackFileMenuItem
                  creative={creative as BannersnackCreative}
                  type={'animated-gif'}
                />
                <DownloadBannersnackFileMenuItem
                  creative={creative as BannersnackCreative}
                  type={'mp4'}
                />
              </>
            )}
            <ContextMenu.Divider />
          </>
        )}
        <ContextMenu.Item
          onClick={toggleArchiveModalOpen}
          prefix={getIconForAction('archive')}
          theme={'danger'}
        >
          {t('Archive ')}
        </ContextMenu.Item>
      </ContextMenu>
      {isDuplicateModalOpen && (
        <CreativeDuplicateModal
          creative={creative as DisplayCreative}
          eventId={creative.get('event')}
          onClose={toggleIsDuplicateModalOpen}
          onConfirm={handleDuplicate}
        />
      )}
      {isArchiveModalOpen && (
        <ArchiveModalV1
          model={creative}
          onClose={toggleArchiveModalOpen}
          onConfirm={handleArchive}
          t={t}
        />
      )}
    </>
  );
}

export default observer(EventCreativesOptions);
